import { getTotalBatchUnits, getTotalBatchWeight } from "@bcc/components/orders/create-batch-dialog/create-batch-dialog.utils";
import {
  exportOrderWaitingSummaryToCsv,
  getOrderWaitingSummaryProducts,
  setOrderWaitingSummaryDialogTestId,
} from "@bcc/components/orders/order-waiting-summary-dialog/order-waiting-summary-dialog.utils";
import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import { Card, CardContent } from "@bcc/components/ui/card";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import { useConfigState } from "@client-shared/providers/config";
import { usePurchaseOrdersState } from "@client-shared/providers/purchase-orders";
import { ArrowLeft, Download, Printer } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

export function OrderWaitingSummaryView() {
  const { t } = useTranslation();
  const [, , configUtils] = useConfigState();
  const navigate = useNavigate();
  const { carrierAccountId } = useParams<{ carrierAccountId?: string }>();
  const [searchParams] = useSearchParams();
  const [state] = usePurchaseOrdersState();

  const orders = useMemo(() => state.purchaseOrders ?? [], [state.purchaseOrders]);
  const products = useMemo(() => getOrderWaitingSummaryProducts(orders), [orders]);
  const totalUnits = useMemo(() => getTotalBatchUnits(orders), [orders]);
  const totalWeight = useMemo(() => getTotalBatchWeight(orders), [orders]);

  const handleExportCsv = useCallback(() => exportOrderWaitingSummaryToCsv(products, t), [products, t]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const handleBack = useCallback(() => {
    const pathname = carrierAccountId ? `${ROUTE_PATHS.MARKETPLACE}/${carrierAccountId}/orders` : ROUTE_PATHS.ORDERS;
    const search = searchParams.toString();
    navigate(`${pathname}${search ? "?" + search : ""}`);
  }, [carrierAccountId, navigate, searchParams]);

  return (
    <div className="space-y-4">
      <Button variant="ghost" onClick={handleBack} className="print:hidden" data-testid={setOrderWaitingSummaryDialogTestId("back")}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        {t("buttons.back")}
      </Button>

      <Card className="border-primary/20 print:border-0 print:shadow-none" testId={setOrderWaitingSummaryDialogTestId("container")}>
        <CardContent className="space-y-6 p-6 print:p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div data-testid={setOrderWaitingSummaryDialogTestId("title")}>
              <h1 className="text-2xl font-semibold tracking-tight">{t("views.orderWaitingSummaryDialog.title")}</h1>
            </div>
            <div className="flex gap-2 print:hidden">
              <Button
                variant="outline"
                onClick={handleExportCsv}
                disabled={products.length === 0}
                data-testid={setOrderWaitingSummaryDialogTestId("export-button")}
              >
                <Download className="mr-2 h-4 w-4" />
                {t("views.orderWaitingSummaryDialog.exportCsv")}
              </Button>
              <Button variant="outline" onClick={handlePrint} data-testid={setOrderWaitingSummaryDialogTestId("print")}>
                <Printer className="mr-2 h-4 w-4" />
                {t("views.orderWaitingSummaryDialog.print")}
              </Button>
            </div>
          </div>

          <section
            className="grid grid-cols-3 gap-4 rounded-lg border border-border/60 bg-muted/30 p-4"
            data-testid={setOrderWaitingSummaryDialogTestId("metrics")}
          >
            <MetricRow
              label={t("views.batchSummary.totalOrders")}
              value={orders.length}
              testId={setOrderWaitingSummaryDialogTestId("metric-orders")}
            />
            <MetricRow
              label={t("views.batchSummary.totalUnits")}
              value={totalUnits}
              testId={setOrderWaitingSummaryDialogTestId("metric-units")}
            />
            <MetricRow
              label={t("views.batchSummary.totalWeight")}
              value={t("weight.weightWithSuffix", { grams: totalWeight })}
              testId={setOrderWaitingSummaryDialogTestId("metric-weight")}
            />
          </section>

          <section data-testid={setOrderWaitingSummaryDialogTestId("pick-list")} className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              {t("views.orderWaitingSummaryDialog.pickList")}
            </h2>
            {products.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid={setOrderWaitingSummaryDialogTestId("empty-state")}>
                {t("views.orderWaitingSummaryDialog.noProducts")}
              </p>
            ) : (
              <ul className="space-y-3">
                {products.map((product) => (
                  <li
                    key={product.key}
                    className="rounded-md border border-border/60 p-3 print:break-inside-avoid"
                    data-testid={setOrderWaitingSummaryDialogTestId(`product-${product.key}`)}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="inline-flex flex-wrap items-center gap-2 text-sm font-medium">
                        {product.productTitle}
                        {product.grindType && (
                          <Badge variant="outline">
                            {configUtils?.findTranslatedValue("grind", product.grindType) ?? product.grindType}
                          </Badge>
                        )}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {t("intlNumber", { value: product.totalUnits }) as string} {t("views.orderWaitingSummaryDialog.unitsLabel")} ·{" "}
                        {t("weight.weightWithSuffix", { grams: product.totalWeight })}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </CardContent>
      </Card>
    </div>
  );
}

interface MetricRowProps {
  readonly label: string;
  readonly value: string | number;
  readonly testId: string;
}

function MetricRow({ label, value, testId }: MetricRowProps) {
  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-lg font-semibold">{value}</span>
    </div>
  );
}
